/** Hooks de datos: carga con estado, recarga y polling (SDD §7.3). */
import { useCallback, useEffect, useRef, useState } from 'react'

export interface AsyncState<T> {
  data: T | undefined
  error: Error | null
  loading: boolean
  reload: () => Promise<void>
  setData: (fn: (prev: T | undefined) => T | undefined) => void
}

/** Ejecuta `fn` al montar y cuando cambian `deps`; descarta respuestas viejas. */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[]): AsyncState<T> {
  const [data, setDataState] = useState<T | undefined>(undefined)
  const [error, setError] = useState<Error | null>(null)
  const [loading, setLoading] = useState(true)
  const seq = useRef(0)
  const fnRef = useRef(fn)
  fnRef.current = fn

  const reload = useCallback(async () => {
    const my = ++seq.current
    setLoading(true)
    try {
      const v = await fnRef.current()
      if (my === seq.current) {
        setDataState(v)
        setError(null)
      }
    } catch (e) {
      if (my === seq.current) setError(e instanceof Error ? e : new Error(String(e)))
    } finally {
      if (my === seq.current) setLoading(false)
    }
  }, [])

  useEffect(() => {
    void reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  const setData = useCallback((f: (prev: T | undefined) => T | undefined) => setDataState(f), [])
  return { data, error, loading, reload, setData }
}

/**
 * Llama a `tick` cada `ms` mientras `active` sea verdadero y la pestaña esté
 * visible (no gasta cuota de API en segundo plano).
 */
export function usePolling(tick: () => void, ms: number, active: boolean): void {
  const ref = useRef(tick)
  ref.current = tick
  useEffect(() => {
    if (!active) return
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') ref.current()
    }, ms)
    return () => clearInterval(id)
  }, [ms, active])
}

/** Valor que se actualiza `ms` después del último cambio (búsqueda). */
export function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms)
    return () => clearTimeout(id)
  }, [value, ms])
  return v
}

export function useDocumentTitle(title: string | undefined): void {
  useEffect(() => {
    document.title = title ? `${title} · eco` : 'eco'
  }, [title])
}
